import { Card, DataState, PageHeader, StatCard } from "@vms/ui-web";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface VenueMetrics {
  sessionsToday: number;
  activeDriversThisWeek: number;
  pendingBookings: number;
  hub: {
    online: boolean;
    hubVersion: string | null;
    hubUiServerVersion: string | null;
    lastSeenAt: string | null;
  };
  licence: {
    status: "active" | "suspended" | "revoked";
    plan: string;
    expiresAt: string | null;
  };
  agents: Record<string, { count: number; versions: string[] }>;
}

const LICENCE_TONE: Record<string, "default" | "amber" | "red"> = {
  active: "default",
  suspended: "amber",
  revoked: "red",
};

export function VenueDashboard() {
  const { currentOrg, currentVenue } = useOrgContext();
  const metrics = useQuery<VenueMetrics>(
    currentVenue ? `/api/venues/${currentVenue.id}/metrics` : null,
  );

  if (!currentOrg || !currentVenue) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={currentVenue.name}
        subtitle={currentVenue.address ?? "Venue dashboard."}
      />

      <DataState loading={metrics.loading} error={metrics.error}>
        {metrics.data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatCard label="Sessions today" value={metrics.data.sessionsToday} />
              <StatCard
                label="Active drivers (7d)"
                value={metrics.data.activeDriversThisWeek}
              />
              <StatCard
                label="Hub"
                value={metrics.data.hub.online ? "Online" : "Offline"}
                tone={metrics.data.hub.online ? "default" : "amber"}
                hint={metrics.data.hub.hubVersion ? `v${metrics.data.hub.hubVersion}` : undefined}
              />
              <StatCard
                label="Pending bookings"
                value={metrics.data.pendingBookings}
                tone={metrics.data.pendingBookings > 0 ? "amber" : "default"}
              />
              <StatCard
                label="Licence"
                value={metrics.data.licence.plan}
                tone={LICENCE_TONE[metrics.data.licence.status] ?? "default"}
                hint={
                  metrics.data.licence.expiresAt
                    ? `expires ${new Date(metrics.data.licence.expiresAt).toLocaleDateString()}`
                    : undefined
                }
              />
            </div>

            <Card className="overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 font-semibold text-gray-900">
                Hub agents
              </div>
              {Object.keys(metrics.data.agents).length === 0 ? (
                <div className="px-5 py-6 text-sm text-gray-500">No agents reported yet.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {Object.entries(metrics.data.agents).map(([kind, info]) => (
                    <div key={kind} className="px-5 py-3 flex items-center justify-between text-sm">
                      <div>
                        <div className="font-medium text-gray-900">{kind}</div>
                        <div className="text-xs text-gray-500">
                          {info.versions.length === 0
                            ? "no version reported"
                            : info.versions.join(", ")}
                        </div>
                      </div>
                      <div className="text-sm text-gray-700">
                        {info.count} {info.count === 1 ? "instance" : "instances"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {!metrics.data.hub.online && (
              <Card className="p-5 border-amber-200 bg-amber-50">
                <div className="font-semibold text-amber-900">Hub appears offline</div>
                <div className="text-sm text-amber-800 mt-1">
                  Last seen{" "}
                  {metrics.data.hub.lastSeenAt
                    ? new Date(metrics.data.hub.lastSeenAt).toLocaleString()
                    : "never"}
                  . Investigate via the venue audit page if this persists.
                </div>
              </Card>
            )}
          </>
        )}
      </DataState>
    </div>
  );
}
